import dayjs, { Dayjs } from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import {
  ITariff,
  IProductDetails,
  IUnitRates,
} from "@/models/tariff/tariff.types"
import { Tariff } from "@/models/tariff/tariff"

dayjs.extend(isSameOrAfter)

// Only the start is configured. The strip ends by itself once the tariff data
// already carries newRate, so no cutover date is hardcoded anywhere.
export type ElectricityVatChange =
  { active: false } | { active: true; newRate: number }

export const NO_ELECTRICITY_VAT_CHANGE: ElectricityVatChange = { active: false }

export const isElectricityVatChangeActive = (
  from: string | null | undefined,
  now: Dayjs = dayjs(),
): boolean => {
  if (!from) return false
  const fromDate = dayjs(from)
  return fromDate.isValid() && now.isSameOrAfter(fromDate, "day")
}

// Re-base a VAT-inclusive amount from oldRate to newRate (both decimal fractions).
export const rebaseVatInclusiveRate = (
  value: number,
  oldRate: number,
  newRate: number,
): number => (value / (1 + oldRate)) * (1 + newRate)

export const rebaseUnitRates = (
  unitRates: IUnitRates,
  oldRate: number,
  newRate: number,
): IUnitRates =>
  Object.fromEntries(
    Object.entries(unitRates).map(([name, value]) => [
      name,
      rebaseVatInclusiveRate(value, oldRate, newRate),
    ]),
  ) as IUnitRates

// Unchanged when the carried rate is unknown or already the target — the latter
// is what self-disables the strip once the endpoint prices at the new rate.
export const rebaseElectricityProductDetails = (
  productDetails: IProductDetails,
  newRate: number,
): IProductDetails => {
  const oldRate = productDetails.vatRate
  if (oldRate == null || oldRate === newRate) return productDetails

  return {
    ...productDetails,
    unitRate:
      productDetails.unitRate == null
        ? undefined
        : rebaseVatInclusiveRate(productDetails.unitRate, oldRate, newRate),
    unitRates: productDetails.unitRates
      ? rebaseUnitRates(productDetails.unitRates, oldRate, newRate)
      : productDetails.unitRates,
    standingCharge: rebaseVatInclusiveRate(
      productDetails.standingCharge,
      oldRate,
      newRate,
    ),
    earlyTerminationCharge: rebaseVatInclusiveRate(
      productDetails.earlyTerminationCharge,
      oldRate,
      newRate,
    ),
    vatRate: newRate,
  }
}

// Fresh instance, so re-evaluating cannot compound the adjustment. Export is
// skipped: that rate is paid to the customer, so there is no VAT to strip.
//
// keepExitFee: Junifer levies the current tariff's exit fee on the next bill at
// that bill's rate, which can't be predicted here, so BE's value stands.
export const rebaseElectricityTariff = (
  tariff: ITariff,
  newRate: number,
  keepExitFee = false,
): ITariff => {
  if (tariff.isGas || tariff.isExport) return tariff

  const productDetails = rebaseElectricityProductDetails(
    tariff.productDetails,
    newRate,
  )
  if (productDetails === tariff.productDetails) return tariff

  return new Tariff({
    id: tariff.id,
    code: tariff.code,
    gsp: tariff.gsp,
    display: tariff.display,
    productDetails: keepExitFee
      ? {
          ...productDetails,
          earlyTerminationCharge: tariff.productDetails.earlyTerminationCharge,
        }
      : productDetails,
    isPrepay: tariff.isPrepay,
    isSmartPrepay: tariff.isSmartPrepay,
    isAvailableToPublic: tariff.isAvailableToPublic,
    directDebit: tariff.directDebit,
    durationMonths: tariff.durationMonths,
    createdAt: tariff.createdAt,
  })
}

export const vatRatePercentLabel = (rate: number): string =>
  `${Math.round(rate * 100)}%`

// Reads the factual rate the agreement endpoint applied, as a fraction
// (0.05 / 0), or null when it gives no rate.
export const formatVatLabel = (vatRate?: number | null): string | null =>
  vatRate == null ? null : `incl. ${vatRatePercentLabel(vatRate)} VAT`

// The rate a set of displayed tariffs carries, read off the first one that
// exposes it — the endpoint reports it per product, but a fuel's products never
// price at mixed rates, so the first speaks for the rest. Export is skipped: its
// rate is paid to the customer. Undefined only when no tariff gives a rate.
export const carriedVatRate = (
  tariffs: (ITariff | undefined)[],
): number | undefined =>
  tariffs.find(
    (tariff) =>
      !!tariff && !tariff.isExport && tariff.productDetails.vatRate != null,
  )?.productDetails.vatRate

export interface FuelVatRate {
  fuel: "Electricity" | "Gas"
  rate?: number
}

// A single-rate claim is only made when every displayed fuel exposes a rate
// and they agree; otherwise each fuel with a known rate speaks for itself.
export const vatInclusiveNote = (fuels: FuelVatRate[]): string => {
  const known = fuels.flatMap((fuel) =>
    fuel.rate === undefined ? [] : [{ fuel: fuel.fuel, rate: fuel.rate }],
  )
  if (known.length === 0) return "All charges shown are inclusive of VAT"

  const rates = new Set(known.map(({ rate }) => rate))
  if (known.length === fuels.length && rates.size === 1) {
    return `All charges shown are inclusive of ${vatRatePercentLabel(known[0].rate)} VAT`
  }

  return known
    .map(
      ({ fuel, rate }, index) =>
        `${index === 0 ? fuel : fuel.toLowerCase()} charges are inclusive of ${vatRatePercentLabel(rate)} VAT`,
    )
    .join("; ")
}
